import React, { useState } from "react";
import { motion } from "framer-motion";
import { MegaphoneIcon, ClipboardListIcon, SparklesIcon, ArrowRightIcon, SendIcon, FileTextIcon, DownloadIcon } from "lucide-react";
import { TopBar } from "./TopBar";
import { ClickHint } from "./ClickHint";
import { AGENT, ELM, MAIN, BRAND } from "../lib/data";
type AgentHomeProps = {
  onOpenOrders: () => void;
};
const cardBase = "group relative flex flex-col justify-between overflow-hidden rounded-2xl border border-neutral-200 bg-white p-6 text-left transition-all hover:-translate-y-0.5 hover:border-neutral-300 hover:shadow-lg hover:shadow-neutral-200/60";
export function AgentHome({
  onOpenOrders
}: AgentHomeProps) {
  return <div className="min-h-full w-full bg-neutral-50 text-neutral-900" data-id="element-89" data-mp="3aeqyopwykv97">
      <TopBar data-id="element-90" data-mp="2mppn4tstx3zp" />
      <div className="mx-auto flex max-w-[1400px] flex-col gap-6 px-5 py-6 sm:px-8 lg:flex-row" data-id="element-91" data-mp="2mpfv70o84mrd">
        {/* Main content (~two thirds) */}
        <main className="flex-1 lg:w-2/3" data-id="element-92" data-mp="2hquwx6sj8d5k">
          {/* Welcome header */}
          <section className="overflow-hidden rounded-2xl border border-neutral-200 bg-white" data-id="element-93" data-mp="3nwyh0679bvtw">
            <div className="flex flex-col gap-5 p-6 sm:p-7" data-id="element-94" data-mp="3svjgg3eoqbha">
              <div data-id="element-95" data-mp="1if4kf1ueqq2">
                <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold" style={{
                backgroundColor: BRAND.accentSoft,
                color: BRAND.accentText
              }} data-id="element-96" data-mp="1hx04v511672">
                  <SparklesIcon className="h-3.5 w-3.5" data-id="element-97" data-mp="350zzejbthg14" /> New listing
                </span>
                <h1 className="mt-3 font-heading text-2xl font-bold tracking-tight text-neutral-900 sm:text-[28px]" data-id="element-98" data-mp="39y8koss9i06l">
                  Welcome back, {AGENT.firstName} — congrats on your new listing at {ELM.address}!
                </h1>
                <p className="mt-1.5 text-[15px] text-neutral-500" data-id="element-99" data-mp="u746uhemx23e">
                  {ELM.type} · {ELM.price} · {ELM.listedAgo}
                </p>
              </div>
              {/* Photo strip */}
              <div className="grid grid-cols-4 gap-2.5" data-id="element-100" data-mp="2v8tnz2jdejnm">
                {ELM.photos.map((src, i) => <div key={i} className={`relative overflow-hidden rounded-xl ${i === 0 ? "col-span-2 row-span-1" : ""}`} data-id="element-101" data-mp="2qaicyohca33t" data-mpi={i}>
                    <img src={src} alt={`${ELM.address} photo ${i + 1}`} className="h-24 w-full object-cover sm:h-28" data-id="element-102" data-mp="2zaniet2z2rfc" />
                  </div>)}
              </div>
            </div>
          </section>

          {/* Action cards */}
          <h2 className="mb-3 mt-7 text-sm font-semibold uppercase tracking-wide text-neutral-400" data-id="element-103" data-mp="2lbxn9izpr1tm">
            Guided paths
          </h2>
          <div className="grid gap-4 sm:grid-cols-3" data-id="element-104" data-mp="2gi2ttnub38hc">
            <ClickHint onClick={onOpenOrders} label="Orders" rounded="rounded-2xl" className="h-full" data-id="element-105" data-mp="1sttgzbf2lv2c">
              <div className={`${cardBase} h-full`} data-id="element-106" data-mp="1nugtcogdkjyp">
                <div data-id="element-107" data-mp="1ntnqvzog8gmd">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl text-white" style={{
                  backgroundColor: BRAND.accent
                }} data-id="element-108" data-mp="3cxuvbmxv78i">
                    <MegaphoneIcon className="h-5 w-5" data-id="element-109" data-mp="3qip2ofahxoiz" />
                  </span>
                  <h3 className="mt-4 text-base font-semibold text-neutral-900" data-id="element-110" data-mp="3vh9w9vv8t9yg">
                    Start Marketing for {ELM.address}
                  </h3>
                  <p className="mt-1 text-sm text-neutral-500" data-id="element-111" data-mp="n1ykz35jxn1g">
                    Ads, print, and social — live in 24 hours
                  </p>
                </div>
                <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold" style={{
                color: BRAND.accentText
              }} data-id="element-112" data-mp="d42bsgw6d25z">
                  Get started <ArrowRightIcon className="h-4 w-4 transition group-hover:translate-x-0.5" data-id="element-113" data-mp="i4gi9ui4o41w" />
                </span>
              </div>
            </ClickHint>

            <motion.button onClick={onOpenOrders} whileTap={{
            scale: 0.98
          }} className={cardBase} data-id="element-114" data-mp="32tmcgj1sqncv">
              <div data-id="element-115" data-mp="3i39kt453mwf8">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-neutral-900 text-white" data-id="element-116" data-mp="3i3sqnmhh5ucw">
                  <ClipboardListIcon className="h-5 w-5" data-id="element-117" data-mp="3d9es5b7in740" />
                </span>
                <h3 className="mt-4 text-base font-semibold text-neutral-900" data-id="element-118" data-mp="39afpxyd65og9">My Orders &amp; Support</h3>
                <p className="mt-1 text-sm text-neutral-500" data-id="element-119" data-mp="34co0dazzyef8">
                  Track everything in progress, get help fast
                </p>
              </div>
              <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-neutral-700" data-id="element-120" data-mp="turhbi5ydre">
                View orders <ArrowRightIcon className="h-4 w-4 transition group-hover:translate-x-0.5" data-id="element-121" data-mp="3rz46y98j79mr" />
              </span>
            </motion.button>

            <motion.div whileTap={{
            scale: 0.98
          }} className={cardBase} data-id="element-122" data-mp="3n3p0418xydzu">
              <div data-id="element-123" data-mp="2gmjz3aaa6qre">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-neutral-900 text-white" data-id="element-124" data-mp="2giw4s8x0i0bm">
                  <SparklesIcon className="h-5 w-5" data-id="element-125" data-mp="21ih4g31brlco" />
                </span>
                <h3 className="mt-4 text-base font-semibold text-neutral-900" data-id="element-126" data-mp="21ge5gti72hvt">Explore Marketing Services</h3>
                <p className="mt-1 text-sm text-neutral-500" data-id="element-127" data-mp="2bgesoqj6pi5a">
                  Listing Concierge, Luxury Marketing, and more
                </p>
              </div>
              <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-neutral-700" data-id="element-128" data-mp="2g8odjs5fzs0p">
                Browse <ArrowRightIcon className="h-4 w-4 transition group-hover:translate-x-0.5" data-id="element-129" data-mp="2kat6sprzammt" />
              </span>
            </motion.div>
          </div>

          {/* Proactive alert banner */}
          <motion.div initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.15
        }} className="mt-6 flex flex-col items-start justify-between gap-4 rounded-2xl border p-5 sm:flex-row sm:items-center" style={{
          backgroundColor: BRAND.accentSoft,
          borderColor: BRAND.accent + "33"
        }} data-id="element-130" data-mp="2u7o63aw2sr25">
            <div className="flex items-start gap-3" data-id="element-131" data-mp="wrsf8902ed2y">
              <span className="text-xl" data-id="element-132" data-mp="rsfi8vfhbpmn">📣</span>
              <div data-id="element-133" data-mp="rtqlmu50ivd8">
                <p className="text-[15px] font-semibold text-neutral-900" data-id="element-134" data-mp="rua0u33a35nk">
                  We noticed {MAIN.address} doesn&apos;t have any marketing running yet.
                </p>
                <p className="text-sm text-neutral-600" data-id="element-135" data-mp="11p1zzbcie0kg">Want to get it started?</p>
              </div>
            </div>
            <ClickHint onClick={onOpenOrders} badge={false} rounded="rounded-xl" className="shrink-0" data-id="element-136" data-mp="1fnrxut75k49v">
              <span className="block rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90" style={{
              backgroundColor: BRAND.accent
            }} data-id="element-137" data-mp="1ap767i5yusx1">
                Start now
              </span>
            </ClickHint>
          </motion.div>
        </main>

        {/* Chat panel (~one third) */}
        <ChatPanel data-id="element-138" data-mp="1ugc1rw24mfsd" />
      </div>
    </div>;
}
function ChatPanel() {
  const [draft, setDraft] = useState("");
  const chips = [`Send my seller report for ${ELM.address}`, `Start marketing for ${MAIN.address}`, "What's the status of my order?"];
  return <aside className="lg:w-1/3" data-id="element-139" data-mp="243fzzgtqk8iv">
      <div className="flex h-[calc(100vh-7rem)] min-h-[560px] flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white lg:sticky lg:top-[5.5rem]" data-id="element-140" data-mp="242d7j4grly0u">
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-neutral-100 px-5 py-4" data-id="element-141" data-mp="1uba6g2nu5j1k">
          <span className="flex h-9 w-9 items-center justify-center rounded-full text-white" style={{
          backgroundColor: BRAND.accent
        }} data-id="element-142" data-mp="1z4boukn940pw">
            <SparklesIcon className="h-4.5 w-4.5" data-id="element-143" data-mp="1pd8mzgmhzhpk" />
          </span>
          <div data-id="element-144" data-mp="1pbe0vyzhpjjw">
            <p className="text-sm font-semibold text-neutral-900" data-id="element-145" data-mp="1be8zicaq18uz">Hi {AGENT.firstName} 👋</p>
            <p className="text-xs text-neutral-500" data-id="element-146" data-mp="1gd2d04q0czv5">Ask me anything, or tap a shortcut.</p>
          </div>
        </div>

        {/* History */}
        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-5" data-id="element-147" data-mp="1vu0l0tlh74va">
          {/* User message */}
          <div className="flex justify-end" data-id="element-148" data-mp="1qxa98igpevfo">
            <div className="max-w-[85%] rounded-2xl rounded-br-md bg-neutral-900 px-3.5 py-2.5 text-sm text-white" data-id="element-149" data-mp="1qu5uir5m4har">
              Where&apos;s my report from last month?
            </div>
          </div>
          {/* Assistant reply */}
          <div className="flex justify-start" data-id="element-150" data-mp="1cwha1kfgjc68">
            <div className="max-w-[90%] space-y-2.5" data-id="element-151" data-mp="1cyam5f8uaolm">
              <div className="rounded-2xl rounded-bl-md bg-neutral-100 px-3.5 py-2.5 text-sm text-neutral-800" data-id="element-152" data-mp="184g4sigpilpp">
                Found it — here&apos;s your June seller report for {ELM.address}. Want me to email it to your seller?
              </div>
              {/* Report card */}
              <div className="flex items-center gap-3 rounded-xl border border-neutral-200 bg-white p-3 shadow-sm" data-id="element-153" data-mp="u69d5k2cphyh">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg" style={{
                backgroundColor: BRAND.accentSoft,
                color: BRAND.accentText
              }} data-id="element-154" data-mp="p7oe3nyu54bm">
                  <FileTextIcon className="h-5 w-5" data-id="element-155" data-mp="35rid7o70kwpd" />
                </span>
                <div className="min-w-0 flex-1" data-id="element-156" data-mp="3at7pf75z3gf1">
                  <p className="truncate text-sm font-semibold text-neutral-900" data-id="element-157" data-mp="3aqbrhiu4krql">
                    Seller Report — June
                  </p>
                  <p className="truncate text-xs text-neutral-500" data-id="element-158" data-mp="2i4jxisqpxssi">{ELM.address} · PDF · 1.2 MB</p>
                </div>
                <button className="rounded-lg p-2 text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-900" data-id="element-159" data-mp="2n33j2s1eh2kc">
                  <DownloadIcon className="h-4 w-4" data-id="element-160" data-mp="2r22p6fyvb5dd" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Suggestion chips */}
        <div className="space-y-2 border-t border-neutral-100 px-4 pt-3" data-id="element-161" data-mp="1zchlxvsxp2p4">
          {chips.map(chip => <motion.button key={chip} onClick={() => setDraft(chip)} whileTap={{
          scale: 0.97
        }} transition={{
          type: "spring",
          stiffness: 400,
          damping: 22
        }} className="flex w-full items-center gap-2 rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2 text-left text-[13px] font-medium text-neutral-700 transition hover:border-neutral-300 hover:bg-white" data-id="element-162" data-mp="23crvtjjukd99">
              <SparklesIcon className="h-3.5 w-3.5 shrink-0" style={{
            color: BRAND.accentText
          }} data-id="element-163" data-mp="3ie0eolfkjwrs" />
              <span className="truncate" data-id="element-164" data-mp="3meke5vnm7ch3">{chip}</span>
            </motion.button>)}
        </div>

        {/* Input */}
        <div className="p-4" data-id="element-165" data-mp="3rddiaxh0hr4l">
          <div className="flex items-center gap-2 rounded-xl border border-neutral-200 bg-white px-3 py-2 focus-within:border-neutral-400" data-id="element-166" data-mp="3rc2fp0zaypf2">
            <input value={draft} onChange={e => setDraft(e.target.value)} placeholder="Message your assistant…" className="flex-1 bg-transparent text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none" data-id="element-167" data-mp="2yq9ir80ou507" />
            <motion.button whileTap={{
            scale: 0.9
          }} className="flex h-8 w-8 items-center justify-center rounded-lg text-white transition hover:opacity-90" style={{
            backgroundColor: BRAND.accent
          }} aria-label="Send message" data-id="element-168" data-mp="2r5gspn2m5t8u">
              <SendIcon className="h-4 w-4" data-id="element-169" data-mp="2d5xnb216sw08" />
            </motion.button>
          </div>
        </div>
      </div>
    </aside>;
}